import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { RouterModule, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';

import {
  defaultAlertConfig,
  defaultUpgradeAlertConfig,
} from 'src/app/utils/constants';
import { LoadingSpinner } from 'src/app/utils/ui';
import { environment } from 'src/environments/environment';
import { ALERT_SERVICE, AlertConfirm, AlertToast } from './models';
import {
  AnalyticsService,
  AuthUser,
  EventsService,
  FirebaseService,
  LogService,
} from './services';

import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Toast } from 'primeng/toast';

@Component({
  selector: 'app-root',
  providers: [ConfirmationService, MessageService],
  imports: [RouterOutlet, RouterModule, ConfirmDialog, Toast, LoadingSpinner],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  #lastIdentifiedUid = '';

  constructor(
    private readonly log: LogService,
    private readonly analytics: AnalyticsService,
    private readonly events: EventsService,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly firebase: FirebaseService
  ) {
    this.initializeApplication();
    this.analytics.init();
    this.subscribeToMessageServices();
  }

  ngOnInit() {
    // TODO: Set the default locale
    // More languages here: https://github.com/primefaces/primelocale
    // const userLang = navigator.language || "en";
    // let locale: Translation = translations[userLang.toLowerCase()];
    // if (locale === undefined) {
    // Default to en if no matching language is found
    //   locale = translations["en"];
    // }
    // this.config.setTranslation({
    //   ...locale,
    // });
  }

  /** Registers the swiper module */
  ngAfterViewInit(): void {
    this.initializeApplication();
  }

  ngOnDestroy() {
    this.events.destroy(ALERT_SERVICE.confirm);
    this.events.destroy(ALERT_SERVICE.upgrade);
    this.events.destroy(ALERT_SERVICE.toast);
  }

  /** Runs everytime the app boots up */
  initializeApplication() {
    if (!environment.production) {
      console.log('initializeApp()');
    }

    // Identify user listener
    this.firebase.user$
      .pipe(
        filter((user): user is AuthUser => user !== null),
        filter((user) => {
          const isIdentified = user.uid === this.#lastIdentifiedUid;
          if (!isIdentified) {
            this.#lastIdentifiedUid = user.uid;
          }
          return !isIdentified;
        })
      )
      .subscribe((user: AuthUser) => {
        this.log.identifyUser({
          email: user.email,
          id: user.uid,
        });
        this.analytics.identifyUser(user.email ?? '');
      });

    // temporary fix
    this.unregisterServiceWorker();
  }

  /**
   * Subscribes to the events service for Alerts and Toasts
   */
  private subscribeToMessageServices() {
    // confirm
    this.events.subscribe<AlertConfirm>(ALERT_SERVICE.confirm, (o) =>
      this.dialogSubscription(o)
    );

    this.events.subscribe<AlertToast>(ALERT_SERVICE.toast, (o) =>
      this.toastSubscription(o)
    );

    this.events.subscribe<AlertToast>(ALERT_SERVICE.upgrade, (o) =>
      this.dialogUpgradeSubscription(o)
    );
  }

  private dialogSubscription(data: AlertConfirm): void {
    this.confirmationService.confirm({
      ...data,
      ...defaultAlertConfig,
      icon: data.destructive ? 'pi pi-exclamation-triangle' : undefined,
    });
  }

  private dialogUpgradeSubscription(data: AlertConfirm): void {
    this.confirmationService.confirm({
      ...data,
      ...defaultUpgradeAlertConfig,
    });
  }

  private toastSubscription(data: AlertToast): void {
    this.messageService.add({
      ...data,
      life: 3000,
    });
  }

  /**
   * Temporary fix to remove service worker from prior version.
   * OK to remove in future.
   */
  private unregisterServiceWorker() {
    try {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((regs) => {
          for (const reg of regs) {
            reg.unregister();
          }
        });
      }
    } catch (ex) {
      console.error(ex);
    }
  }
}
